/**
 * Camada 1 · os dois estados que se parecem na tela e sao opostos: VAZIO e ERRO.
 *
 * POR QUE OS DOIS MORAM NO MESMO ARQUIVO: eles existem um por causa do outro. Vazio diz
 * "nao ha nada aqui, e esta certo"; erro diz "eu nao consegui olhar". Confundir os dois e
 * o defeito mais comum de tela de lista, e a unica defesa e a peca que os separa estar a
 * mao quando alguem escreve a tela.
 *
 * O DEFEITO MEDIDO QUE COMPROU ESTE ARQUIVO, num sistema legado: a camada de dados
 * engole o erro e devolve lista vazia em pelo menos tres telas (usuarios, plano de acao e
 * reavaliacao). Falha de servidor e organizacao sem gente caem na MESMA tela, e quem
 * opera conclui que nao ha dado quando o que houve foi uma consulta que nao respondeu.
 *
 * A regra desta casa e que ausencia de dado e DITA, nunca preenchida. Isso vale para os
 * dois lados: a tela precisa dizer que esta vazia, e precisa dizer quando esta cega.
 *
 * O estilo `.cx-vazio` ja existia e ja era usado SOLTO em tres componentes (`Table`,
 * `DueList`, `BarChart`), cada um repetindo o markup a mao. Estes componentes nao mexem
 * naqueles: sao acrescimo, e a unificacao fica registrada como divida.
 */

import type { ReactNode } from 'react';
// O botao vem da PECA da casa, e nao de um `<button>` cru: o lint recusa botao solto em
// `apps/**` justamente para o estado de foco, o alvo de 44px e a forma de capsula nao
// dependerem de quem escreve a tela.
import { Botao } from './Button.js';

export interface EstadoVazioProps {
  /** O que nao ha. Uma frase, nao uma palavra: "Nenhum ciclo ainda" diz mais que "Vazio". */
  readonly titulo: string;
  /**
   * O PORQUE, e e ele que faz este componente valer a pena.
   *
   * Tela vazia sem explicacao deixa a pessoa sem saber se ela fez algo errado, se o
   * sistema falhou, ou se e assim mesmo. Uma linha dizendo de onde o dado viria resolve
   * as tres duvidas de uma vez.
   */
  readonly children: ReactNode;
  /**
   * A acao que tira a tela do vazio, quando existe uma.
   *
   * OPCIONAL DE PROPOSITO: nem todo vazio tem saida a um clique. Vazio que oferece botao
   * inutil e pior que vazio sem botao, porque promete o que nao cumpre.
   */
  readonly acao?: ReactNode;
}

export function EstadoVazio({ titulo, children, acao }: EstadoVazioProps) {
  return (
    // `role="status"` e nao `alert`: vazio e informacao, e nao urgencia. `status` anuncia
    // sem interromper o que a pessoa estiver ouvindo.
    //
    // A borda TRACEJADA distingue de relance do cartao comum: ela diz "este espaco existe
    // e esta reservado", enquanto a borda continua diria "este e o conteudo".
    <div role="status" className="cx-vazio rounded-cx-lg border border-dashed border-borda-forte p-cx-6">
      <p className="text-cx-base font-medium text-texto">{titulo}</p>
      <div className="mt-cx-2 text-cx-sm text-texto-suave">{children}</div>
      {acao && <div className="mt-cx-4">{acao}</div>}
    </div>
  );
}

export interface EstadoErroProps {
  /** O que falhou, em uma frase. */
  readonly titulo: string;
  /**
   * A MENSAGEM DO SERVIDOR, quando existe, e ela vai CRUA.
   *
   * Traduzir erro de infraestrutura para linguagem amigavel apaga a unica informacao que
   * permite alguem resolver. "Algo deu errado" nao ajuda ninguem; o texto que o banco
   * devolveu ajuda quem opera e quem mantem.
   *
   * Ausente, o componente diz que nao houve mensagem, em vez de deixar o espaco em branco
   * e parecer que alguem esqueceu de escrever.
   */
  readonly mensagem?: string;
  /** A explicacao em linguagem de gente, acima da linha tecnica. */
  readonly children?: ReactNode;
}

export function EstadoErro({ titulo, mensagem, children }: EstadoErroProps) {
  return (
    // `role="alert"` aqui SIM: falha de leitura muda o que a pessoa pode concluir da tela,
    // e ela precisa saber sem procurar.
    // VIDRO E NAO VERMELHO CHEIO, pelo padrao fechado em 09/09/2026, e este bloco e o caso
    // mais caro dele: ele ocupa a largura toda da area util, e vermelho opaco num campo desse
    // tamanho e literalmente o *"VERMELHO GLASS APPLE ESTA MUITO FORTE"* que o fundador
    // apontou. O sinal passa a vir da BORDA, que continua na cor de erro.
    //
    // O TEXTO NAO GANHA ALPHA, e por isso esta troca nao mexe no contraste: o titulo e
    // `text-texto` e o corpo e `text-texto-suave`, os dois neutros. Quem carrega a informacao
    // e o texto, e tinta no portador da informacao e o erro que esta rodada existe para nao
    // cometer.
    <div
      role="alert"
      className={
        'rounded-cx-lg border border-erro/50 bg-erro/15 p-cx-6 ' +
        'dark:border-erro/35 dark:bg-erro/20 backdrop-blur-vidro backdrop-saturate-vidro'
      }
    >
      <p className="text-cx-base font-medium text-texto">{titulo}</p>
      {children && <div className="mt-cx-2 text-cx-sm text-texto-suave">{children}</div>}
      {/*
        A LINHA TECNICA SO APARECE QUANDO HA O QUE DIZER, e isso mudou em 14/09/2026.

        ANTES ELA ERA SEMPRE DESENHADA, e sem mensagem imprimia "O servidor não devolveu
        mensagem." O fundador viu isso na tela de Equipe, embaixo de um aviso de sessão
        vencida: a frase é sobre o PROTOCOLO, não sobre o que houve, e quem opera não decide
        nada com ela. Pior, ela ocupava o lugar onde a pessoa procuraria o que fazer agora.

        Monoespacada quando existe, de propósito: é texto de máquina, e a fonte diz isso
        antes de a pessoa ler. Também preserva espaçamento, que costuma importar em mensagem
        de banco.
      */}
      {mensagem && mensagem.trim() !== '' && (
        <p className="mt-cx-3 font-mono text-cx-xs text-texto-suave">{mensagem}</p>
      )}
    </div>
  );
}

/**
 * A SESSAO VENCEU, e isto NAO e o servico fora do ar.
 *
 * NASCEU EM 14/09/2026, de uma captura do fundador na tela de Equipe: ele leu *"Nao foi
 * possivel falar com o servico"* com o sistema inteiro no ar. O servico tinha falado, e
 * falado certo: devolveu 401 porque a sessao dele havia vencido. A tela transformou uma
 * informacao ("entre de novo") num alarme de infraestrutura ("o sistema caiu").
 *
 * POR QUE UM COMPONENTE E NAO UM `EstadoErro` COM OUTRO TITULO: a ACAO e diferente, e e a
 * acao que decide o desenho. Servico fora pede esperar; sessao vencida pede entrar de novo,
 * agora, e sem um botao a pessoa fica olhando um aviso que nao leva a lugar nenhum. Foi
 * exatamente o que a captura mostrava.
 *
 * ELE NAO E VERMELHO, de proposito: nada quebrou. Vermelho e para falha, e usa-lo aqui
 * gasta o unico sinal que a pessoa tem de que algo esta errado de verdade.
 */
export interface EstadoSessaoExpiradaProps {
  /** O que fazer ao confirmar. Tipicamente recarregar, que e quem decide a sessao. */
  readonly aoEntrarDeNovo: () => void;
  /** O texto do botao, traduzido pela tela que o usa. */
  readonly rotulo?: string;
  /** O titulo, traduzido pela tela. O padrao serve a quem nao traduz. */
  readonly titulo?: string;
  /** A explicacao, traduzida pela tela. */
  readonly children?: ReactNode;
}

export function EstadoSessaoExpirada({
  aoEntrarDeNovo,
  rotulo = 'Entrar de novo',
  titulo = 'Sua sessão expirou',
  children = 'Entre de novo para continuar de onde você estava.',
}: EstadoSessaoExpiradaProps) {
  return (
    <div
      role="status"
      className={
        'rounded-cx-lg border border-alerta/50 bg-alerta/15 p-cx-6 ' +
        'dark:border-alerta/35 dark:bg-alerta/20 backdrop-blur-vidro backdrop-saturate-vidro'
      }
    >
      <p className="text-cx-base font-medium text-texto">{titulo}</p>
      <div className="mt-cx-2 text-cx-sm text-texto-suave">{children}</div>
      <div className="mt-cx-3">
        <Botao onClick={aoEntrarDeNovo}>{rotulo}</Botao>
      </div>
    </div>
  );
}

